import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Support = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: "hello@qwip.co.uk",
          subject: `Support request from ${name}`,
          type: "support",
          name,
          fromEmail: email,
          message,
        },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Failed to send");
      }

      setSent(true);
      toast.success("Message sent — we'll be in touch soon");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err: any) {
      console.error("Support form error:", err);
      toast.error("Couldn't send your message. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-funnel font-semibold text-primary mb-3">
              how can we help?
            </h1>
          </motion.div>

          {/* WhatsApp button */}
          <motion.a
            href="https://wa.me/447347474059"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-primary text-primary-foreground rounded-full font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all mb-4"
          >
            <MessageCircle className="w-5 h-5" />
            message us on WhatsApp
          </motion.a>

          <div className="flex items-center gap-3 my-8">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-foreground/50">or send us an email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Contact form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-card border border-border rounded-2xl p-6 md:p-8 space-y-4"
          >
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground/80 mb-1.5">
                your name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="jane doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground/80 mb-1.5">
                your email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground/80 mb-1.5">
                how can we help?
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
                rows={5}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                placeholder="tell us what's going on..."
              />
            </div>

            <Button
              type="submit"
              disabled={loading || sent}
              size="lg"
              className="w-full rounded-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  sending...
                </>
              ) : sent ? (
                <>
                  <Check className="w-4 h-4" />
                  message sent
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  send message
                </>
              )}
            </Button>

            <p className="text-xs text-center text-foreground/50 pt-2">
              or email us directly at{" "}
              <a href="mailto:hello@qwip.co.uk" className="underline hover:text-primary">
                hello@qwip.co.uk
              </a>
            </p>
          </motion.form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Support;
